/**
 * RolesPage — CRUD for roles; policySpecs editor (reuses PolicyBuilder);
 * assign dialog (user only); assignments table with TTL countdown
 * + Refresh/Unassign (epic-6 §3 / PB-1).
 */
import { useEffect, useRef, useState } from 'react';
import { type ColumnDef } from '@tanstack/react-table';
import { Plus, Pencil, Trash2, UserPlus, ShieldCheck, RefreshCw, Ban } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { DataTable } from '@/components/shared/DataTable';
import { EmptyState } from '@/components/shared/EmptyState';
import { LoadingSpinner } from '@/components/shared/LoadingSpinner';
import { ConfirmDialog } from '@/components/shared/ConfirmDialog';
import { useToast } from '@/components/shared/Toast';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  useGetRolesQuery,
  useCreateRoleMutation,
  useUpdateRoleMutation,
  useDeleteRoleMutation,
  useAssignRoleMutation,
  useUnassignRoleMutation,
  useRefreshRoleMutation,
  useGetRoleAssignmentsQuery,
} from '@/store/api/rolesApi';
import { useGetUsersQuery } from '@/store/api/apiSlice';
import { PolicyBuilder, type PolicyBuilderValue, requiresExtendExpiry, findIncompleteStatement, POLICY_TEMPLATES } from '@/components/policies/PolicyBuilder';
import type { Role, PolicySpec, PolicyStatement, RoleAssignment } from '@/types';
import { format } from 'date-fns';

/** Convert a policySpec's `Array<Record<string, unknown>>` statements to PolicyStatement[]. */
function toStatements(statements: Array<Record<string, unknown>>): PolicyStatement[] {
  return statements.map((s) => ({
    effect: (s.effect as PolicyStatement['effect']) ?? 'Allow',
    actions: Array.isArray(s.actions) ? (s.actions as string[]) : [],
    resources: Array.isArray(s.resources) ? (s.resources as string[]) : [],
    conditions: (s.conditions as PolicyStatement['conditions']) ?? [],
  }));
}

/** Live TTL countdown (updates every second). */
function useNow(): number {
  const [now, setNow] = useState(Date.now());
  const timer = useRef<ReturnType<typeof setInterval> | null>(null);
  useEffect(() => {
    timer.current = setInterval(() => setNow(Date.now()), 1000);
    return () => {
      if (timer.current) clearInterval(timer.current);
    };
  }, []);
  return now;
}

function formatCountdown(expiresAt: string | undefined, now: number): string {
  if (!expiresAt) return '—';
  const ms = new Date(expiresAt).getTime() - now;
  if (ms <= 0) return 'expired';
  const totalSeconds = Math.floor(ms / 1000);
  const h = Math.floor(totalSeconds / 3600);
  const m = Math.floor((totalSeconds % 3600) / 60);
  const s = totalSeconds % 60;
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${pad(h)}:${pad(m)}:${pad(s)}`;
}

export function RolesPage() {
  const toast = useToast();
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<Role | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<Role | null>(null);
  const [selectedRole, setSelectedRole] = useState<Role | null>(null);
  const [assignTarget, setAssignTarget] = useState<Role | null>(null);
  const [specEditor, setSpecEditor] = useState<{ role: Role; specIndex: number } | null>(null);

  const { data: roles, isLoading } = useGetRolesQuery();
  const [createRole] = useCreateRoleMutation();
  const [updateRole] = useUpdateRoleMutation();
  const [deleteRole, { isLoading: isDeleting }] = useDeleteRoleMutation();

  const roleList = (roles as Role[] | undefined) ?? [];

  const [form, setForm] = useState({ name: '', description: '' });
  const [specs, setSpecs] = useState<PolicySpec[]>([]);

  const columns: ColumnDef<Role, unknown>[] = [
    {
      accessorKey: 'name',
      header: 'Name',
      cell: ({ row }) => (
        <div>
          <p className="font-medium">{row.original.name}</p>
          {row.original.description && (
            <p className="text-xs text-muted-foreground truncate max-w-xs">{row.original.description}</p>
          )}
        </div>
      ),
    },
    {
      accessorKey: 'policySpecs',
      header: 'Policy specs',
      cell: ({ row }) => <Badge variant="outline" className="text-xs">{row.original.policySpecs?.length ?? 0}</Badge>,
    },
    {
      accessorKey: 'orgId',
      header: 'Org',
      cell: ({ row }) => (row.original.orgId ? <span className="font-mono text-xs">{row.original.orgId.slice(0, 8)}…</span> : <span className="text-xs text-muted-foreground">—</span>),
    },
    {
      accessorKey: 'isActive',
      header: 'Status',
      cell: ({ row }) => (
        <Badge variant={row.original.isActive ? 'default' : 'secondary'} className="text-xs">
          {row.original.isActive ? 'active' : 'inactive'}
        </Badge>
      ),
    },
    {
      accessorKey: 'updatedAt',
      header: 'Updated',
      cell: ({ row }) => format(new Date(row.original.updatedAt), 'MMM d, yyyy'),
    },
    {
      id: 'actions',
      header: '',
      cell: ({ row }) => (
        <div className="flex gap-1">
          <Button variant="ghost" size="icon" onClick={(e) => { e.stopPropagation(); setAssignTarget(row.original); }} title="Assign">
            <UserPlus className="h-4 w-4" />
          </Button>
          <Button variant="ghost" size="icon" onClick={(e) => { e.stopPropagation(); openEdit(row.original); }}>
            <Pencil className="h-4 w-4" />
          </Button>
          <Button variant="ghost" size="icon" onClick={(e) => { e.stopPropagation(); setDeleteTarget(row.original); }}>
            <Trash2 className="h-4 w-4 text-destructive" />
          </Button>
        </div>
      ),
    },
  ];

  function openCreate() {
    setEditing(null);
    setForm({ name: '', description: '' });
    setSpecs([]);
    setModalOpen(true);
  }

  function openEdit(role: Role) {
    setEditing(role);
    setForm({ name: role.name, description: role.description ?? '' });
    setSpecs(role.policySpecs ?? []);
    setModalOpen(true);
  }

  async function handleSave() {
    if (!form.name.trim()) {
      toast.error('Name is required');
      return;
    }
    // Airtight save guard: every policySpec statement must define >=1 action and
    // >=1 resource — the authorization backplane rejects empty collections at
    // assign-time (HTTP 400), so a role with an incomplete spec must never save.
    for (let i = 0; i < specs.length; i++) {
      const incomplete = findIncompleteStatement(specs[i].statements);
      if (incomplete !== null) {
        toast.error(
          `Policy spec "${specs[i].name}" statement ${incomplete + 1} must define at least 1 action and 1 resource`,
        );
        return;
      }
    }
    const body = {
      name: form.name.trim(),
      description: form.description.trim() || undefined,
      policySpecs: specs,
    };
    try {
      if (editing) {
        await updateRole({ id: editing._id, body }).unwrap();
      } else {
        await createRole(body).unwrap();
      }
      setModalOpen(false);
    } catch (err) {
      toast.error((err as { data?: { message?: string } })?.data?.message ?? 'Failed to save role');
    }
  }

  async function handleDelete() {
    if (deleteTarget) {
      try {
        await deleteRole(deleteTarget._id).unwrap();
        setDeleteTarget(null);
      } catch (err) {
        toast.error((err as { data?: { message?: string } })?.data?.message ?? 'Failed to delete role');
      }
    }
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Roles</h1>
          <p className="text-sm text-muted-foreground">
            Roles, policy specs and assignments with TTL management
          </p>
        </div>
        <Button onClick={openCreate}>
          <Plus className="mr-2 h-4 w-4" />
          Create Role
        </Button>
      </div>

      <Card>
        <CardContent className="pt-6">
          {isLoading ? (
            <LoadingSpinner className="py-12" />
          ) : roleList.length === 0 ? (
            <EmptyState
              title="No roles"
              description="Create a role to define reusable policy specs"
              action={<Button onClick={openCreate}>Create Role</Button>}
            />
          ) : (
            <DataTable columns={columns} data={roleList} onRowClick={setSelectedRole} />
          )}
        </CardContent>
      </Card>

      {selectedRole && (
        <RoleDetailPanel role={selectedRole} onEdit={openEdit} onClose={() => setSelectedRole(null)} onAssign={() => setAssignTarget(selectedRole)} onOpenSpecEditor={(index) => setSpecEditor({ role: selectedRole, specIndex: index })} />
      )}

      {/* Create / edit dialog with policySpecs */}
      <Dialog open={modalOpen} onOpenChange={setModalOpen}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editing ? 'Edit Role' : 'Create Role'}</DialogTitle>
            <DialogDescription>Role details and its policy specs.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Name</Label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
            </div>
            <div className="space-y-2">
              <Label>Description</Label>
              <textarea
                className="flex min-h-[70px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
              />
            </div>
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label>Policy specs</Label>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() =>
                    setSpecs([
                      ...specs,
                      {
                        name: `spec-${specs.length + 1}`,
                        // Prefilled with a real catalog grant so the new spec is
                        // immediately assignable — the authorization backplane
                        // rejects statements with empty actions/resources (400).
                        statements: [
                          {
                            effect: 'Allow' as const,
                            actions: [...POLICY_TEMPLATES[0].policy.statements[0].actions],
                            resources: [...POLICY_TEMPLATES[0].policy.statements[0].resources],
                            conditions: [],
                          },
                        ],
                        ttlSeconds: 300,
                      },
                    ])
                  }
                >
                  <Plus className="mr-1 h-3 w-3" /> Add spec
                </Button>
              </div>
              {specs.length === 0 ? (
                <p className="text-xs text-muted-foreground">No policy specs yet.</p>
              ) : (
                <div className="space-y-1.5">
                  {specs.map((spec, i) => (
                    <div key={i} className="flex items-center justify-between rounded-md border px-3 py-2">
                      <div>
                        <p className="text-sm font-medium">{spec.name}</p>
                        <p className="text-xs text-muted-foreground">
                          {spec.statements.length} statement(s) · TTL {formatTtl(spec.ttlSeconds)}
                        </p>
                      </div>
                      <div className="flex gap-1">
                        <Button variant="ghost" size="sm" className="h-7 text-xs" onClick={() => setSpecEditor({ role: editing ?? ({ _id: '_draft', name: form.name, ownerUserId: '', policySpecs: specs, isActive: true, createdAt: '', updatedAt: '' } as Role), specIndex: i })}>
                          Edit
                        </Button>
                        <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => setSpecs(specs.filter((_, idx) => idx !== i))}>
                          <Trash2 className="h-3.5 w-3.5 text-destructive" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setModalOpen(false)}>Cancel</Button>
            <Button onClick={handleSave}>Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* PolicySpec editor (reuses PolicyBuilder, statements-only) */}
      {specEditor && (
        <SpecEditorDialog
          spec={specs[specEditor.specIndex] ?? specEditor.role.policySpecs?.[specEditor.specIndex]}
          onClose={() => setSpecEditor(null)}
          onSave={(next) => {
            const nextSpecs = specEditor.role._id === '_draft'
              ? specs.map((s, i) => (i === specEditor.specIndex ? next : s))
              : (specEditor.role.policySpecs ?? []).map((s, i) => (i === specEditor.specIndex ? next : s));
            if (specEditor.role._id === '_draft') {
              setSpecs(nextSpecs);
            } else {
              void updateRole({ id: specEditor.role._id, body: { policySpecs: nextSpecs } })
                .unwrap()
                .then(() => toast.success('Policy spec updated'))
                .catch((err) => toast.error((err as { data?: { message?: string } })?.data?.message ?? 'Failed to update spec'));
            }
            setSpecEditor(null);
          }}
        />
      )}

      {/* Assign dialog */}
      {assignTarget && (
        <AssignDialog role={assignTarget} onClose={() => setAssignTarget(null)} />
      )}

      <ConfirmDialog
        open={!!deleteTarget}
        onOpenChange={(open) => !open && setDeleteTarget(null)}
        title="Delete Role"
        description={`Delete role "${deleteTarget?.name}"? This revokes its active assignments.`}
        confirmLabel="Delete"
        variant="destructive"
        onConfirm={handleDelete}
        isLoading={isDeleting}
      />
    </div>
  );
}

// ─── Role detail (policy specs + assignments) ─────────────────────────────────

export function RoleDetailPanel(props: { role: Role; onEdit: (r: Role) => void; onClose: () => void; onAssign: () => void; onOpenSpecEditor: (index: number) => void }) {
  const toast = useToast();
  const now = useNow();
  const { data: assignments, isLoading } = useGetRoleAssignmentsQuery(props.role._id);
  const [unassign, { isLoading: isUnassigning }] = useUnassignRoleMutation();
  const [refresh, { isLoading: isRefreshing }] = useRefreshRoleMutation();
  const { data: users } = useGetUsersQuery({ page: 1, pageSize: 100 });
  const [unassignTarget, setUnassignTarget] = useState<RoleAssignment | null>(null);

  const userList =
    ((users as { data?: Array<{ _id: string; username: string; email?: string }> } | undefined)?.data ?? []) as Array<{
      _id: string;
      username: string;
      email?: string;
    }>;
  const assignmentList = (assignments as RoleAssignment[] | undefined) ?? [];

  const assignColumns: ColumnDef<RoleAssignment, unknown>[] = [
    {
      accessorKey: 'userId',
      header: 'User',
      cell: ({ row }) => userList.find((u) => u._id === row.original.userId)?.username ?? row.original.userId,
    },
    {
      accessorKey: 'status',
      header: 'Status',
      cell: ({ row }) => (
        <Badge variant={row.original.status === 'active' ? 'default' : 'secondary'} className="text-xs">
          {row.original.status}
        </Badge>
      ),
    },
    {
      accessorKey: 'policyIds',
      header: 'Ephemeral policies',
      cell: ({ row }) => <Badge variant="outline" className="text-xs">{row.original.policyIds?.length ?? 0}</Badge>,
    },
    {
      accessorKey: 'expiresAt',
      header: 'TTL',
      cell: ({ row }) => (
        <span className={`font-mono text-xs ${row.original.expiresAt && new Date(row.original.expiresAt).getTime() <= now ? 'text-destructive' : 'text-muted-foreground'}`}>
          {formatCountdown(row.original.expiresAt, now)}
        </span>
      ),
    },
    {
      accessorKey: 'assignedAt',
      header: 'Assigned',
      cell: ({ row }) => (row.original.assignedAt ? format(new Date(row.original.assignedAt), 'MMM d, yyyy') : '—'),
    },
    {
      id: 'actions',
      header: '',
      // Refresh/Unassign only apply to live assignments; revoked rows would
      // 404 ("No active assignment…") because the backend rejects them.
      cell: ({ row }) =>
        row.original.status === 'active' ? (
          <div className="flex gap-1">
            <Button
              variant="ghost"
              size="icon"
              title="Refresh (renew TTL)"
              onClick={() =>
                refresh({ id: props.role._id, userId: row.original.userId })
                  .unwrap()
                  .then(() => toast.success('Assignment refreshed'))
                  .catch((err) => toast.error((err as { data?: { message?: string } })?.data?.message ?? 'Refresh failed'))
              }
              disabled={isRefreshing}
            >
              <RefreshCw className="h-4 w-4" />
            </Button>
            <Button variant="ghost" size="icon" title="Unassign" onClick={() => setUnassignTarget(row.original)}>
              <Ban className="h-4 w-4 text-destructive" />
            </Button>
          </div>
        ) : (
          <span className="text-xs text-muted-foreground">—</span>
        ),
    },
  ];

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-3">
        <CardTitle className="text-base flex items-center gap-2">
          <ShieldCheck className="h-4 w-4" /> {props.role.name}
          <Badge variant="outline" className="text-[10px]">{props.role._id}</Badge>
        </CardTitle>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={() => props.onEdit(props.role)}><Pencil className="mr-1 h-3.5 w-3.5" /> Edit</Button>
          <Button variant="outline" size="sm" onClick={props.onAssign}><UserPlus className="mr-1 h-3.5 w-3.5" /> Assign</Button>
          <Button variant="ghost" size="sm" onClick={props.onClose}>Close</Button>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        <Tabs defaultValue="specs">
          <TabsList>
            <TabsTrigger value="specs">Policy specs ({props.role.policySpecs?.length ?? 0})</TabsTrigger>
            <TabsTrigger value="assignments">Assignments ({assignmentList.length})</TabsTrigger>
          </TabsList>
          <TabsContent value="specs">
            {!props.role.policySpecs || props.role.policySpecs.length === 0 ? (
              <EmptyState title="No policy specs" description="Add a spec to define this role's ephemeral policies" />
            ) : (
              <div className="space-y-2">
                {(props.role.policySpecs ?? []).map((spec, i) => (
                  <div key={i} className="flex items-center justify-between rounded-md border px-3 py-2">
                    <div>
                      <p className="text-sm font-medium">{spec.name}</p>
                      <p className="text-xs text-muted-foreground">
                        {spec.statements.length} statement(s) · TTL {formatTtl(spec.ttlSeconds)}
                        {requiresExtendExpiry(spec.ttlSeconds) ? ' ⚠ >1h needs extend-expiry' : ''}
                      </p>
                    </div>
                    <Button variant="outline" size="sm" onClick={() => props.onOpenSpecEditor(i)}>Edit</Button>
                  </div>
                ))}
              </div>
            )}
          </TabsContent>
          <TabsContent value="assignments">
            {isLoading ? (
              <LoadingSpinner className="py-8" />
            ) : assignmentList.length === 0 ? (
              <EmptyState title="No assignments" description="Assign this role to a user to materialize its ephemeral policies" />
            ) : (
              <DataTable columns={assignColumns} data={assignmentList} />
            )}
          </TabsContent>
        </Tabs>
      </CardContent>

      <ConfirmDialog
        open={!!unassignTarget}
        onOpenChange={(open) => !open && setUnassignTarget(null)}
        title="Unassign Role"
        description={`Revoke role from ${unassignTarget?.userId}? Its ephemeral policies will be revoked.`}
        confirmLabel="Unassign"
        variant="destructive"
        onConfirm={async () => {
          if (unassignTarget) {
            try {
              await unassign({ id: props.role._id, userId: unassignTarget.userId }).unwrap();
              toast.success('Assignment revoked');
            } catch (err) {
              toast.error((err as { data?: { message?: string } })?.data?.message ?? 'Failed to unassign');
            }
            setUnassignTarget(null);
          }
        }}
        isLoading={isUnassigning}
      />
    </Card>
  );
}

// ─── Policy spec editor dialog ────────────────────────────────────────────────

function SpecEditorDialog(props: { spec?: PolicySpec; onClose: () => void; onSave: (next: PolicySpec) => void }) {
  const [value, setValue] = useState<PolicyBuilderValue>({
    name: props.spec?.name ?? 'spec',
    statements: props.spec ? toStatements(props.spec.statements) : [],
  });
  const [ttl, setTtl] = useState(props.spec?.ttlSeconds ?? 300);
  const [saveError, setSaveError] = useState<string | null>(null);

  const handleSaveSpec = () => {
    // Airtight save guard: every statement must define >=1 action and >=1
    // resource (authorization backplane rejects empty collections with 400).
    const incomplete = findIncompleteStatement(value.statements);
    if (incomplete !== null) {
      setSaveError(`Statement ${incomplete + 1} must define at least 1 action and 1 resource`);
      return;
    }
    setSaveError(null);
    props.onSave({
      name: value.name?.trim() || props.spec?.name || 'spec',
      statements: value.statements as unknown as Array<Record<string, unknown>>,
      ttlSeconds: ttl,
    });
  };

  return (
    <Dialog open onOpenChange={(open) => !open && props.onClose()}>
      <DialogContent className="max-w-3xl max-h-[88vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Policy spec — {props.spec?.name ?? 'new'}</DialogTitle>
          <DialogDescription>
            Statements are materialized as ephemeral policies when the role is assigned.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="flex gap-3">
            <div className="flex-1 space-y-1">
              <Label className="text-xs">Spec name</Label>
              <Input value={value.name ?? ''} onChange={(e) => setValue({ ...value, name: e.target.value })} />
            </div>
            <div className="w-44 space-y-1">
              <Label className="text-xs">TTL (seconds)</Label>
              <Input type="number" min={60} value={ttl} onChange={(e) => setTtl(Number(e.target.value) || 300)} />
              {requiresExtendExpiry(ttl) && (
                <p className="text-[11px] text-amber-600">&gt;1h requires extend-expiry permission</p>
              )}
            </div>
          </div>
          <PolicyBuilder
            value={value}
            onChange={(next) => {
              setValue(next);
              setSaveError(null);
            }}
            showPolicyLevel={false}
            showTemplates={false}
            policies={[]}
          />
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={props.onClose}>Cancel</Button>
          <Button onClick={handleSaveSpec}>
            Save spec
          </Button>
        </DialogFooter>
        {saveError && (
          <p className="mt-2 text-xs font-medium text-destructive">{saveError}</p>
        )}
      </DialogContent>
    </Dialog>
  );
}

// ─── Assign dialog ────────────────────────────────────────────────────────────

function AssignDialog(props: { role: Role; onClose: () => void }) {
  const toast = useToast();
  const [assign, { isLoading }] = useAssignRoleMutation();
  const { data: users } = useGetUsersQuery({ page: 1, pageSize: 100 });
  const [userId, setUserId] = useState('');

  const userList =
    ((users as { data?: Array<{ _id: string; username: string; email?: string }> } | undefined)?.data ?? []) as Array<{
      _id: string;
      username: string;
      email?: string;
    }>;

  const submit = async () => {
    if (!userId) {
      toast.warning('Select a user');
      return;
    }
    try {
      await assign({ id: props.role._id, userId }).unwrap();
      toast.success('Role assigned — ephemeral policies materialized');
      props.onClose();
    } catch (err) {
      toast.error((err as { data?: { message?: string } })?.data?.message ?? 'Assignment failed');
    }
  };

  return (
    <Dialog open onOpenChange={(open) => !open && props.onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Assign role — {props.role.name}</DialogTitle>
          <DialogDescription>
            The role is granted to the user for as long as the assignment is held.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="space-y-2">
            <Label>User</Label>
            <select className="flex h-10 w-full rounded-md border border-input bg-background px-3 text-sm" value={userId} onChange={(e) => setUserId(e.target.value)}>
              <option value="">— select user —</option>
              {userList.map((u) => (
                <option key={u._id} value={u._id}>{u.username} ({u.email ?? ''})</option>
              ))}
            </select>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={props.onClose}>Cancel</Button>
          <Button onClick={submit} disabled={isLoading}>{isLoading ? 'Assigning…' : 'Assign'}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

function formatTtl(seconds?: number): string {
  if (!seconds) return '∞';
  if (seconds % 86400 === 0) return `${seconds / 86400}d`;
  if (seconds % 3600 === 0) return `${seconds / 3600}h`;
  if (seconds % 60 === 0) return `${seconds / 60}m`;
  return `${seconds}s`;
}